import { useState, useEffect, useRef } from 'react';
import { Satellite, Loader2, AlertCircle, Check, MapPin, RefreshCw, Radio, Eye, Bookmark, BookmarkCheck, Trash2, ScanSearch } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Preset satellites with NORAD IDs (TLEs fetched live from CelesTrak)
const SATELLITE_PRESETS = {
  iss: { name: 'ISS (ZARYA)', norad_id: 25544 },
  hubble: { name: 'HST', norad_id: 20580 },
  starlink: { name: 'STARLINK-1234', norad_id: 44238 },
};

export function TLEParser({ onOrbitParsed, onRealTimeSatellite, onLocationSet, onOverheadSatellites, isPremium }) {
  const { user } = useAuth();
  const [line1, setLine1] = useState('');
  const [line2, setLine2] = useState('');
  const [satName, setSatName] = useState('');
  const [loading, setLoading] = useState(false);
  const [fetchingTLE, setFetchingTLE] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [address, setAddress] = useState('');
  const [locationLoading, setLocationLoading] = useState(false);
  const [location, setLocation] = useState(null);
  const refreshRef = useRef(null);

  // What's Overhead state
  const [overheadLoading, setOverheadLoading] = useState(false);
  const [overheadResults, setOverheadResults] = useState(null);
  const [overheadError, setOverheadError] = useState(null);

  // Saved Orbits state
  const [savedOrbits, setSavedOrbits] = useState([]);
  const [savingOrbit, setSavingOrbit] = useState(null);

  // Load saved orbits on mount if logged in
  useEffect(() => {
    if (user) fetchSavedOrbits();
  }, [user]);

  const fetchSavedOrbits = async () => {
    try {
      const res = await fetch(`${API_URL}/api/user/saved-orbits`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setSavedOrbits(data.orbits || []);
      }
    } catch { /* silent */ }
  };

  const saveOrbit = async (sat) => {
    if (!user) return;
    setSavingOrbit(sat.satellite_name || sat.norad_id);
    try {
      const res = await fetch(`${API_URL}/api/user/saved-orbits`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          satellite_name: sat.satellite_name,
          norad_id: sat.norad_id,
          tle_line1: sat.tle_line1,
          tle_line2: sat.tle_line2,
        }),
      });
      if (res.ok) await fetchSavedOrbits();
    } catch { /* silent */ }
    setSavingOrbit(null);
  };

  const deleteOrbit = async (name) => {
    try {
      const res = await fetch(`${API_URL}/api/user/saved-orbits/${encodeURIComponent(name)}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (res.ok) setSavedOrbits(prev => prev.filter(o => o.satellite_name !== name));
    } catch { /* silent */ }
  };

  const loadSavedOrbit = (orbit) => {
    setLine1(orbit.tle_line1);
    setLine2(orbit.tle_line2);
    setSatName(orbit.satellite_name);
  };

  const loadSample = async (key) => {
    const preset = SATELLITE_PRESETS[key];
    setError(null);
    setResult(null);
    setFetchingTLE(true);
    try {
      const res = await fetch(`${API_URL}/api/tle/fetch?norad_id=${preset.norad_id}`);
      if (!res.ok) throw new Error('Could not fetch live TLE data');
      const data = await res.json();
      setLine1(data.tle_line1);
      setLine2(data.tle_line2);
      setSatName(data.satellite_name);
    } catch (e) {
      setError(`Failed to fetch live TLE: ${e.message}`);
    } finally {
      setFetchingTLE(false);
    }
  };

  const propagateTLE = async () => {
    if (!line1.trim() || !line2.trim()) {
      setError('Please enter both TLE lines');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/api/tle/propagate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tle_line1: line1, tle_line2: line2, satellite_name: satName || null }),
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || 'Failed to propagate TLE');
      }
      const data = await res.json();
      setResult(data);
      setAutoRefresh(true);
      if (onRealTimeSatellite) {
        onRealTimeSatellite({
          name: satName || data.satellite_name || 'SAT',
          pos3d: data.display.pos3d,
          lat: data.position.lat,
          lon: data.position.lon,
          alt_km: data.position.alt_km,
          speed_km_s: data.position.speed_km_s,
          groundTrack: data.ground_track,
        });
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  // Auto-refresh every 2 seconds for real-time tracking
  useEffect(() => {
    if (autoRefresh && result && line1 && line2) {
      refreshRef.current = setInterval(propagateTLE, 2000);
      return () => clearInterval(refreshRef.current);
    }
    return () => clearInterval(refreshRef.current);
  }, [autoRefresh, line1, line2]); // eslint-disable-line react-hooks/exhaustive-deps

  const geocodeAddress = async () => {
    if (!address.trim()) return;
    setLocationLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/geocode?q=${encodeURIComponent(address)}`);
      if (!res.ok) throw new Error('Address not found');
      const data = await res.json();
      setLocation(data);
      if (onLocationSet) onLocationSet(data);
    } catch {
      setLocation(null);
    } finally {
      setLocationLoading(false);
    }
  };

  const scanOverhead = async () => {
    if (!location) return;
    setOverheadLoading(true);
    setOverheadError(null);
    setOverheadResults(null);
    try {
      const res = await fetch(`${API_URL}/api/satellites/overhead?lat=${location.lat}&lon=${location.lon}`);
      if (!res.ok) throw new Error('Failed to scan');
      const data = await res.json();
      setOverheadResults(data);
      if (onOverheadSatellites) onOverheadSatellites(data.overhead || []);
    } catch (e) {
      setOverheadError(e.message);
    } finally {
      setOverheadLoading(false);
    }
  };

  const trackOverheadSat = async (sat) => {
    setLine1(sat.tle_line1);
    setLine2(sat.tle_line2);
    setSatName(sat.satellite_name);
    // Propagate directly with the sat's TLE data
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/api/tle/propagate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tle_line1: sat.tle_line1, tle_line2: sat.tle_line2, satellite_name: sat.satellite_name }),
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || 'Failed to propagate TLE');
      }
      const data = await res.json();
      setResult(data);
      setAutoRefresh(true);
      if (onRealTimeSatellite) {
        onRealTimeSatellite({
          name: sat.satellite_name || data.satellite_name || 'SAT',
          pos3d: data.display.pos3d,
          lat: data.position.lat,
          lon: data.position.lon,
          alt_km: data.position.alt_km,
          speed_km_s: data.position.speed_km_s,
          groundTrack: data.ground_track,
        });
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const isSaved = (name) => savedOrbits.some(o => o.satellite_name === name);

  if (!isPremium) {
    return (
      <div className="p-4 rounded-lg" style={{ background: 'rgba(232,121,249,0.05)', border: '1px solid rgba(232,121,249,0.2)' }}>
        <div className="flex items-center gap-2 mb-2">
          <Satellite size={16} color="#E879F9" />
          <span style={{ fontFamily: 'JetBrains Mono', fontSize: '11px', color: '#E879F9', letterSpacing: '0.15em' }}>SATELLITE TRACKER</span>
          <span className="px-2 py-0.5 rounded text-xs" style={{ background: 'rgba(255,215,0,0.2)', color: '#FFD700', fontFamily: 'Rajdhani', fontWeight: 600 }}>PREMIUM</span>
        </div>
        <p style={{ fontFamily: 'Outfit', fontSize: '13px', color: 'rgba(255,255,255,0.5)', lineHeight: 1.5 }}>
          Track real satellites in real-time using TLE data. See where they are right now over Earth.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Location Input — moved to top since overhead needs it */}
      <div className="p-4 rounded-lg" style={{ background: 'rgba(255,215,0,0.03)', border: '1px solid rgba(255,215,0,0.12)' }}>
        <div className="flex items-center gap-2 mb-3">
          <MapPin size={14} color="#FFD700" />
          <span style={{ fontFamily: 'JetBrains Mono', fontSize: '11px', color: '#FFD700', letterSpacing: '0.15em' }}>YOUR LOCATION</span>
        </div>
        <div className="flex gap-2">
          <input type="text" value={address} onChange={e => setAddress(e.target.value)}
            placeholder="Enter your address or city..."
            onKeyDown={e => e.key === 'Enter' && geocodeAddress()}
            data-testid="location-input"
            className="flex-1 px-3 py-1.5 rounded"
            style={{ background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.1)', color: '#fff', fontFamily: 'Outfit', fontSize: '12px', minWidth: 0 }} />
          <button onClick={geocodeAddress} disabled={locationLoading} data-testid="locate-btn"
            className="px-3 py-1.5 rounded flex items-center gap-1"
            style={{ background: 'rgba(255,215,0,0.15)', border: '1px solid rgba(255,215,0,0.3)', color: '#FFD700', cursor: 'pointer', fontFamily: 'Rajdhani', fontWeight: 600, fontSize: '12px', flexShrink: 0 }}>
            {locationLoading ? <Loader2 size={12} className="animate-spin" /> : <MapPin size={12} />}
            Find
          </button>
        </div>
        {location && (
          <div className="mt-2 flex items-center gap-2">
            <Check size={12} color="#00FF94" />
            <span style={{ fontFamily: 'JetBrains Mono', fontSize: '10px', color: 'rgba(255,255,255,0.5)' }}>
              {location.display_name?.split(',').slice(0, 2).join(',')} ({location.lat.toFixed(2)}, {location.lon.toFixed(2)})
            </span>
          </div>
        )}
      </div>

      {/* What's Overhead? */}
      <div className="p-4 rounded-lg" style={{ background: 'rgba(255,149,0,0.04)', border: '1px solid rgba(255,149,0,0.15)' }}>
        <div className="flex items-center gap-2 mb-3">
          <ScanSearch size={14} color="#FF9500" />
          <span style={{ fontFamily: 'JetBrains Mono', fontSize: '11px', color: '#FF9500', letterSpacing: '0.15em' }}>WHAT'S OVERHEAD?</span>
        </div>

        <button onClick={scanOverhead} disabled={!location || overheadLoading} data-testid="scan-overhead-btn"
          className="w-full py-2 rounded flex items-center justify-center gap-2"
          style={{
            background: location ? 'rgba(255,149,0,0.2)' : 'rgba(255,255,255,0.03)',
            border: `1px solid ${location ? 'rgba(255,149,0,0.4)' : 'rgba(255,255,255,0.08)'}`,
            color: location ? '#FF9500' : 'rgba(255,255,255,0.25)',
            cursor: location ? 'pointer' : 'not-allowed',
            fontFamily: 'Rajdhani', fontWeight: 600, fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.08em',
          }}>
          {overheadLoading ? <Loader2 size={14} className="animate-spin" /> : <ScanSearch size={14} />}
          {overheadLoading ? 'Scanning...' : !location ? 'Set location first' : 'Scan for satellites'}
        </button>

        {overheadError && (
          <div className="mt-2 flex items-center gap-2" style={{ color: '#FF4444' }}>
            <AlertCircle size={14} />
            <span style={{ fontFamily: 'JetBrains Mono', fontSize: '11px' }}>{overheadError}</span>
          </div>
        )}

        {overheadResults && (
          <div className="mt-3">
            <p style={{ fontFamily: 'JetBrains Mono', fontSize: '9px', color: 'rgba(255,255,255,0.35)', letterSpacing: '0.1em', marginBottom: '8px' }}>
              CHECKED {overheadResults.checked} SATELLITES &middot; {overheadResults.found} OVERHEAD
            </p>
            {overheadResults.overhead.length === 0 ? (
              <p style={{ fontFamily: 'Outfit', fontSize: '12px', color: 'rgba(255,255,255,0.4)', textAlign: 'center', padding: '12px 0' }}>
                No notable satellites overhead right now. Try again in a few minutes!
              </p>
            ) : (
              <div className="space-y-2">
                {overheadResults.overhead.map((sat) => (
                  <div key={sat.norad_id} className="rounded-lg p-3" data-testid={`overhead-sat-${sat.norad_id}`}
                    style={{ background: 'rgba(0,0,0,0.25)', border: '1px solid rgba(255,149,0,0.12)' }}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#FF9500' }} />
                        <span style={{ fontFamily: 'Rajdhani', fontWeight: 700, fontSize: '13px', color: '#fff', textTransform: 'uppercase' }}>
                          {sat.satellite_name}
                        </span>
                      </div>
                      <span style={{ fontFamily: 'JetBrains Mono', fontSize: '9px', color: 'rgba(255,255,255,0.3)' }}>
                        #{sat.norad_id}
                      </span>
                    </div>
                    <div className="grid grid-cols-3 gap-1 mb-2">
                      {[
                        ['ALT', `${Math.round(sat.alt_km)} km`],
                        ['DIST', `${Math.round(sat.distance_km)} km`],
                        ['POS', `${sat.lat.toFixed(1)}, ${sat.lon.toFixed(1)}`],
                      ].map(([label, val]) => (
                        <div key={label} className="px-2 py-1 rounded" style={{ background: 'rgba(255,149,0,0.06)' }}>
                          <p style={{ fontFamily: 'JetBrains Mono', fontSize: '7px', color: 'rgba(255,255,255,0.3)', letterSpacing: '0.12em' }}>{label}</p>
                          <p style={{ fontFamily: 'JetBrains Mono', fontSize: '10px', color: '#FF9500' }}>{val}</p>
                        </div>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <button onClick={() => trackOverheadSat(sat)} data-testid={`track-overhead-${sat.norad_id}`}
                        className="flex-1 py-1.5 rounded flex items-center justify-center gap-1"
                        style={{ background: 'rgba(0,240,255,0.1)', border: '1px solid rgba(0,240,255,0.25)', color: '#00F0FF', cursor: 'pointer', fontFamily: 'Rajdhani', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>
                        <Eye size={11} /> Track
                      </button>
                      {user && (
                        <button onClick={() => saveOrbit(sat)} disabled={isSaved(sat.satellite_name) || savingOrbit === (sat.satellite_name || sat.norad_id)}
                          data-testid={`save-overhead-${sat.norad_id}`}
                          className="py-1.5 px-3 rounded flex items-center justify-center gap-1"
                          style={{
                            background: isSaved(sat.satellite_name) ? 'rgba(0,255,148,0.1)' : 'rgba(255,215,0,0.1)',
                            border: `1px solid ${isSaved(sat.satellite_name) ? 'rgba(0,255,148,0.25)' : 'rgba(255,215,0,0.25)'}`,
                            color: isSaved(sat.satellite_name) ? '#00FF94' : '#FFD700',
                            cursor: isSaved(sat.satellite_name) ? 'default' : 'pointer',
                            fontFamily: 'Rajdhani', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase',
                          }}>
                          {isSaved(sat.satellite_name) ? <BookmarkCheck size={11} /> : <Bookmark size={11} />}
                          {isSaved(sat.satellite_name) ? 'Saved' : 'Save'}
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* TLE Input */}
      <div className="p-4 rounded-lg" style={{ background: 'rgba(232,121,249,0.05)', border: '1px solid rgba(232,121,249,0.2)' }}>
        <div className="flex items-center gap-2 mb-3">
          <Radio size={14} color="#E879F9" className={result ? 'animate-pulse' : ''} />
          <span style={{ fontFamily: 'JetBrains Mono', fontSize: '11px', color: '#E879F9', letterSpacing: '0.15em' }}>REAL-TIME SATELLITE TRACKER</span>
        </div>

        {/* Samples - fetched live from CelesTrak */}
        <div className="flex gap-2 mb-3 flex-wrap items-center">
          <span style={{ fontFamily: 'JetBrains Mono', fontSize: '9px', color: 'rgba(255,255,255,0.4)' }}>LOAD:</span>
          {Object.entries(SATELLITE_PRESETS).map(([key, { name }]) => (
            <button key={key} onClick={() => loadSample(key)} disabled={fetchingTLE} className="px-2 py-1 rounded text-xs"
              style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.6)', cursor: fetchingTLE ? 'wait' : 'pointer', fontFamily: 'JetBrains Mono', fontSize: '10px', opacity: fetchingTLE ? 0.5 : 1 }}>
              {name}
            </button>
          ))}
          {fetchingTLE && <Loader2 size={12} className="animate-spin" color="#E879F9" />}
        </div>

        <input type="text" value={satName} onChange={e => setSatName(e.target.value)} placeholder="Satellite name (optional)"
          data-testid="tle-name"
          className="w-full px-3 py-1.5 rounded mb-2"
          style={{ background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.1)', color: '#fff', fontFamily: 'JetBrains Mono', fontSize: '11px' }} />
        <input type="text" value={line1} onChange={e => setLine1(e.target.value)} placeholder="TLE Line 1"
          data-testid="tle-line1"
          className="w-full px-3 py-1.5 rounded mb-2"
          style={{ background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.1)', color: '#fff', fontFamily: 'JetBrains Mono', fontSize: '11px' }} />
        <input type="text" value={line2} onChange={e => setLine2(e.target.value)} placeholder="TLE Line 2"
          data-testid="tle-line2"
          className="w-full px-3 py-1.5 rounded mb-2"
          style={{ background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.1)', color: '#fff', fontFamily: 'JetBrains Mono', fontSize: '11px' }} />

        <div className="flex gap-2">
          <button onClick={propagateTLE} disabled={loading} data-testid="parse-tle-btn"
            className="flex-1 py-2 rounded flex items-center justify-center gap-2"
            style={{ background: 'rgba(232,121,249,0.3)', border: '1px solid rgba(232,121,249,0.5)', color: '#E879F9', cursor: loading ? 'wait' : 'pointer', fontFamily: 'Rajdhani', fontWeight: 600, fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
            {loading ? <Loader2 size={14} className="animate-spin" /> : <Satellite size={14} />}
            {loading ? 'Propagating...' : 'Track Satellite'}
          </button>
          {user && result && line1 && line2 && (
            <button onClick={() => saveOrbit({ satellite_name: satName || result?.satellite_name || 'Unknown', tle_line1: line1, tle_line2: line2 })}
              disabled={isSaved(satName || result?.satellite_name)}
              data-testid="save-tracked-orbit-btn"
              className="py-2 px-3 rounded flex items-center justify-center gap-1"
              style={{
                background: isSaved(satName || result?.satellite_name) ? 'rgba(0,255,148,0.1)' : 'rgba(255,215,0,0.15)',
                border: `1px solid ${isSaved(satName || result?.satellite_name) ? 'rgba(0,255,148,0.3)' : 'rgba(255,215,0,0.3)'}`,
                color: isSaved(satName || result?.satellite_name) ? '#00FF94' : '#FFD700',
                cursor: isSaved(satName || result?.satellite_name) ? 'default' : 'pointer',
                fontFamily: 'Rajdhani', fontWeight: 600, fontSize: '12px', textTransform: 'uppercase',
              }}>
              {isSaved(satName || result?.satellite_name) ? <BookmarkCheck size={14} /> : <Bookmark size={14} />}
            </button>
          )}
        </div>

        {error && (
          <div className="mt-2 flex items-center gap-2" style={{ color: '#FF4444' }}>
            <AlertCircle size={14} />
            <span style={{ fontFamily: 'JetBrains Mono', fontSize: '11px' }}>{error}</span>
          </div>
        )}
      </div>

      {/* Real-time Results */}
      {result && (
        <div className="p-4 rounded-lg" style={{ background: 'rgba(0,240,255,0.03)', border: '1px solid rgba(0,240,255,0.15)' }}>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#00FF94', animation: 'pulse 2s infinite' }} />
              <span style={{ fontFamily: 'Rajdhani', fontWeight: 700, fontSize: '14px', color: '#fff', textTransform: 'uppercase' }}>
                {result.satellite_name || 'SATELLITE'}
              </span>
            </div>
            <button
              onClick={() => setAutoRefresh(v => !v)}
              data-testid="auto-refresh-toggle"
              className="flex items-center gap-1 px-2 py-1 rounded"
              style={{ background: autoRefresh ? 'rgba(0,255,148,0.1)' : 'rgba(255,255,255,0.05)', border: `1px solid ${autoRefresh ? 'rgba(0,255,148,0.3)' : 'rgba(255,255,255,0.1)'}`, cursor: 'pointer', fontFamily: 'JetBrains Mono', fontSize: '9px', color: autoRefresh ? '#00FF94' : 'rgba(255,255,255,0.4)' }}>
              <RefreshCw size={10} className={autoRefresh ? 'animate-spin' : ''} />
              {autoRefresh ? 'LIVE' : 'AUTO'}
            </button>
          </div>

          {/* Position Data */}
          <div className="grid grid-cols-2 gap-2 mb-3">
            {[
              ['LAT', `${result.position.lat.toFixed(2)}`],
              ['LON', `${result.position.lon.toFixed(2)}`],
              ['ALT', `${result.position.alt_km.toLocaleString()} km`],
              ['SPEED', `${result.position.speed_km_s.toFixed(1)} km/s`],
            ].map(([label, value]) => (
              <div key={label} className="px-3 py-2 rounded-lg" style={{ background: 'rgba(0,0,0,0.2)' }}>
                <p style={{ fontFamily: 'JetBrains Mono', fontSize: '8px', color: 'rgba(255,255,255,0.35)', letterSpacing: '0.15em' }}>{label}</p>
                <p style={{ fontFamily: 'JetBrains Mono', fontSize: '13px', color: '#00F0FF', fontWeight: 500 }}>{value}</p>
              </div>
            ))}
          </div>

          {/* Orbital Elements */}
          <div className="rounded-lg overflow-hidden" style={{ border: '1px solid rgba(255,255,255,0.06)' }}>
            {[
              ['PERIGEE', `${result.orbital_elements.perigee_km.toLocaleString()} km`],
              ['APOGEE', `${result.orbital_elements.apogee_km.toLocaleString()} km`],
              ['PERIOD', `${result.orbital_elements.period_minutes.toFixed(1)} min`],
              ['INCLINATION', `${result.orbital_elements.inclination}`],
            ].map(([label, val], i, arr) => (
              <div key={label} className="flex justify-between px-3 py-1.5"
                style={{ background: i % 2 === 0 ? 'rgba(255,255,255,0.02)' : 'transparent', borderBottom: i < arr.length - 1 ? '1px solid rgba(255,255,255,0.04)' : 'none' }}>
                <span style={{ fontFamily: 'JetBrains Mono', fontSize: '9px', color: 'rgba(255,255,255,0.3)', letterSpacing: '0.1em' }}>{label}</span>
                <span style={{ fontFamily: 'JetBrains Mono', fontSize: '10px', color: '#E879F9' }}>{val}</span>
              </div>
            ))}
          </div>

          {/* Distance from user location */}
          {location && result && (
            <div className="mt-3 px-3 py-2 rounded-lg flex items-center gap-2" style={{ background: 'rgba(255,215,0,0.05)', border: '1px solid rgba(255,215,0,0.15)' }}>
              <MapPin size={12} color="#FFD700" />
              <div>
                <p style={{ fontFamily: 'JetBrains Mono', fontSize: '8px', color: 'rgba(255,255,255,0.35)', letterSpacing: '0.1em' }}>DISTANCE FROM YOU</p>
                <p style={{ fontFamily: 'JetBrains Mono', fontSize: '12px', color: '#FFD700' }}>
                  ~{Math.round(Math.sqrt(
                    Math.pow((result.position.lat - location.lat) * 111, 2) +
                    Math.pow((result.position.lon - location.lon) * 111 * Math.cos(location.lat * Math.PI / 180), 2) +
                    Math.pow(result.position.alt_km, 2)
                  )).toLocaleString()} km
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Saved Orbits */}
      {user && savedOrbits.length > 0 && (
        <div className="p-4 rounded-lg" style={{ background: 'rgba(0,255,148,0.03)', border: '1px solid rgba(0,255,148,0.12)' }}>
          <div className="flex items-center gap-2 mb-3">
            <BookmarkCheck size={14} color="#00FF94" />
            <span style={{ fontFamily: 'JetBrains Mono', fontSize: '11px', color: '#00FF94', letterSpacing: '0.15em' }}>SAVED ORBITS</span>
            <span className="px-1.5 py-0.5 rounded" style={{ background: 'rgba(0,255,148,0.15)', fontFamily: 'JetBrains Mono', fontSize: '9px', color: '#00FF94' }}>
              {savedOrbits.length}
            </span>
          </div>
          <div className="space-y-1.5">
            {savedOrbits.map((orbit) => (
              <div key={orbit.satellite_name} className="flex items-center gap-2 rounded-lg px-3 py-2"
                data-testid={`saved-orbit-${orbit.satellite_name}`}
                style={{ background: 'rgba(0,0,0,0.2)', border: '1px solid rgba(255,255,255,0.05)' }}>
                <div style={{ width: 5, height: 5, borderRadius: '50%', background: '#00FF94', flexShrink: 0 }} />
                <span className="flex-1 truncate" style={{ fontFamily: 'Rajdhani', fontWeight: 600, fontSize: '12px', color: 'rgba(255,255,255,0.7)', textTransform: 'uppercase' }}>
                  {orbit.satellite_name}
                </span>
                <button onClick={() => loadSavedOrbit(orbit)} data-testid={`load-saved-${orbit.satellite_name}`}
                  className="px-2 py-1 rounded flex items-center gap-1"
                  style={{ background: 'rgba(0,240,255,0.1)', border: '1px solid rgba(0,240,255,0.2)', color: '#00F0FF', cursor: 'pointer', fontFamily: 'JetBrains Mono', fontSize: '9px' }}>
                  <Eye size={10} /> TRACK
                </button>
                <button onClick={() => deleteOrbit(orbit.satellite_name)} data-testid={`delete-saved-${orbit.satellite_name}`}
                  className="p-1 rounded"
                  style={{ background: 'rgba(255,68,68,0.08)', border: '1px solid rgba(255,68,68,0.15)', color: '#FF4444', cursor: 'pointer' }}>
                  <Trash2 size={10} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
